import { useState } from 'react'

import * as Sentry from '@sentry/browser'
import { UpdateFillableDocumentDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import { DocumentViewerContentProps } from 'modules/documents/components/DocumentPreview'
import DocumentPreviewLayout from 'modules/documents/components/DocumentPreview/Layout'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { canFill, isInTrashBin, isPendingToFill } from 'modules/documents/lib/Documents'

import ReviewPrompt from './ReviewPrompt'

interface Props {
  content?: (props: DocumentViewerContentProps) => React.ReactElement
  documents: Document[]
  index: number
  onClose: () => void
  onNavigateToOtherDocument?: (document: Document | undefined) => void
}

const DocumentNavigator = ({
  onClose,
  content,
  documents,
  index,
  onNavigateToOtherDocument,
}: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const document = documents[index || 0]
  const fillable = !!document && currentEmployee ? canFill(document, currentEmployee.id) : false
  const [currentId, setCurrentId] = useState<string | undefined>(document?.id)
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [disableFilleableConfirm, setDisableFilleableConfirm] = useState(
    !!fillable || (document ? isInTrashBin(document) : false)
  )
  const [updateFillableDocument] = useMutation(UpdateFillableDocumentDocument, {
    extractorFn: (data) => data.documentsMutations.updateFillableDocumentDocumentsDocument,
  })

  const getRemoteDocumentUrl = async () => {
    if (!document) return ''

    const absoluteUrl = document.fileUrl

    try {
      const request = await ApiClient().get(absoluteUrl, {
        inline: true,
        get_url: true,
        source: 'web',
      })
      return request.url
    } catch (error) {
      Sentry.captureException(error)
      return ''
    }
  }

  const logAction = async (action: string): Promise<void> => {
    if (!document) return

    try {
      await ApiClient().post(`/documents/document_log`, {
        id: document.id,
        document_action: action,
        source: 'web',
      })
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const onDownloadPdf = async (): Promise<void> => {
    logAction('downloaded')
  }

  const onPrintPdf = async (): Promise<void> => {
    logAction('print_action')
  }

  const updateUrl = async () => setUrl(await getRemoteDocumentUrl())

  useUnsafeEffect(() => {
    if (!document) return
    if (document.id !== currentId) {
      setCurrentId(document?.id)
      updateUrl()
    }

    if (!!url && isPendingToFill(document)) return

    updateUrl()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document])

  const onClickForward = () => {
    const nextDocument = documents[index + 1]

    if (onNavigateToOtherDocument) {
      onNavigateToOtherDocument(nextDocument)
    }
  }

  const onClickBack = () => {
    const nextDocument = documents[index - 1]

    if (onNavigateToOtherDocument) {
      onNavigateToOtherDocument(nextDocument)
    }
  }

  if (!document) {
    return null
  }

  return (
    <DocumentPreviewLayout
      onClose={onClose}
      beforeFileViewer={<ReviewPrompt document={document} />}
      fileViewerProps={{
        filename: document.filename,
        mimeType: document.contentType || '',
        url,
        isUrlAbsolute: true,
        fillable: fillable && !isInTrashBin(document),
        onCloseFillableModal: () => setDisableFilleableConfirm(false),
        onSavingStatusChange: setSaving,
        onSaveFile: async (file: Blob) => {
          await updateFillableDocument({ variables: { id: document.id, file: file } })
        },
        onDownloadPdf: async () => await onDownloadPdf(),
        onPrintPdf: async () => await onPrintPdf(),
      }}
      content={
        content &&
        content({
          document,
          documents,
          index,
          saving,
          disableFilleableConfirm: disableFilleableConfirm,
          onCancel: onClose,
          onClickBack: onClickBack,
          onClickForward: onClickForward,
        })
      }
    />
  )
}

export default observer(DocumentNavigator)
